/*
 * Copyright Evollis 2022
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import { TENANTS } from "../../../constants"
import Axios from "axios"
import createDecorator from "final-form-calculate"

// Cache local pour conserver les flags supplier même après reset formulaire
let supplierCache = {
  shopId: null,
  productCreationEnabled: false,
  allowModifyProductPrice: true
}

let supplierFlagsInflight = { shopId: null, promise: null }

const syncSupplierFlagsFromShop = async (tunnelData, token, allValues) => {
  const shopId = allValues?.shop?.value?.split("-")?.[0] ?? null

  if (!shopId) {
    supplierFlagsInflight = { shopId: null, promise: null }
    supplierCache = { shopId: null, productCreationEnabled: false, allowModifyProductPrice: true }
    return { productCreationEnabled: false, allowModifyProductPrice: true }
  }

  if (supplierFlagsInflight.shopId === shopId && supplierFlagsInflight.promise) {
    return supplierFlagsInflight.promise
  }

  const promise = (async () => {
    const response = await Axios.get(`${tunnelData.apiUrl}middle-business-service/api/v1/suppliers/id/${shopId}`, {
      headers: { Authorization: `Bearer ${token}`, Accept: "*/*" }
    })

    // Si un autre magasin a été demandé entre-temps, ne pas écraser le cache avec une réponse obsolète
    if (supplierFlagsInflight.shopId !== shopId) {
      return {
        productCreationEnabled: supplierCache.shopId ? supplierCache.productCreationEnabled : false,
        allowModifyProductPrice: supplierCache.shopId ? supplierCache.allowModifyProductPrice : true
      }
    }

    supplierCache.shopId = shopId
    supplierCache.productCreationEnabled = response.data.productCreationEnabled ?? false
    supplierCache.allowModifyProductPrice = response.data.allowModifyProductPrice ?? true

    return {
      productCreationEnabled: supplierCache.productCreationEnabled,
      allowModifyProductPrice: supplierCache.allowModifyProductPrice
    }
  })()

  supplierFlagsInflight = { shopId, promise }

  try {
    return await promise
  } finally {
    if (supplierFlagsInflight.shopId === shopId && supplierFlagsInflight.promise === promise) {
      supplierFlagsInflight = { shopId: null, promise: null }
    }
  }
}

const shopDecorator = (tunnelData, token) => {
  return {
    field: "shop",
    updates: {
      delivery: () => {
        // Le mode de livraison dépend du magasin : on le force à être rechoisi après changement de magasin
        return null
      },
      productCreationEnabled: async (_ignoredValue, allValues) => {
        const { productCreationEnabled } = await syncSupplierFlagsFromShop(tunnelData, token, allValues)
        return productCreationEnabled
      },
      allowModifyProductPrice: async (_ignoredValue, allValues) => {
        const { allowModifyProductPrice } = await syncSupplierFlagsFromShop(tunnelData, token, allValues)
        return allowModifyProductPrice
      }
    }
  }
}

const deliveryDecorator = (tunnelData, token) => {
  return {
    field: "delivery",
    updates: {
      productCreationEnabled: async (_ignoredValue, allValues) => {
        const currentShopId = allValues?.shop?.value?.split("-")?.[0] ?? null
        if (allValues?.delivery?.value) {
          const { productCreationEnabled } = await syncSupplierFlagsFromShop(tunnelData, token, allValues)
          return productCreationEnabled
        }
        // Livraison non choisie : ne pas réinitialiser les flags magasin (produits avant livraison)
        if (currentShopId && supplierCache.shopId === currentShopId) {
          return supplierCache.productCreationEnabled
        }
        return false
      },
      allowModifyProductPrice: async (_ignoredValue, allValues) => {
        const currentShopId = allValues?.shop?.value?.split("-")?.[0] ?? null
        if (allValues?.delivery?.value) {
          const { allowModifyProductPrice } = await syncSupplierFlagsFromShop(tunnelData, token, allValues)
          return allowModifyProductPrice
        }
        if (currentShopId && supplierCache.shopId === currentShopId) {
          return supplierCache.allowModifyProductPrice
        }
        // Par défaut true (historique), mais si on a déjà le magasin en cache on réutilise la valeur
        return supplierCache.shopId ? supplierCache.allowModifyProductPrice : true
      }
    }
  }
}

const skuDecorator = (tunnelData, token) => {
  return {
    field: /products\[\d+\].sku/,
    updates: async (value, name, allValues) => {
      const { apiUrl, tenant } = tunnelData

      const familyField = name.replace(".sku", ".family")
      const categoryField = name.replace(".sku", ".category")
      const priceField = name.replace(".sku", ".price")
      const brandField = name.replace(".sku", ".brand")
      const descriptionField = name.replace(".sku", ".description")
      const quantityField = name.replace(".sku", ".quantity")
      const productIndex = name.replace(/\D/g, "")

      const getFamilyField = async () => {
        if (value) {
          return Axios.get(
            `${tunnelData.apiUrl}middle-business-service/api/v1/categories/by-supplierid/${
              allValues?.shop?.value?.split("-")[0]
            }`,
            { headers: { Authorization: `Bearer ${token}`, Accept: "*/*" } }
          )
            .then((response) => {
              const family = response.data.filter((e) => {
                return (
                  e.parent.id === value.value.categoryEvollis.parentCategoryId ||
                  e.parent.parentCategoryId === value.value.categoryEvollis.parentCategoryId
                )
              })
              return family[0].parent.parentCategoryId
            })
            .catch((_errorFamilyField) => console.log("_errorFamilyField : ", _errorFamilyField))
        } else {
          return null
        }
      }

      const getCategoryField = async (familyId) => {
        if (value) {
          return Axios.get(
            `${tunnelData.apiUrl}middle-business-service/api/v1/categories/by-category/${
              allValues?.shop?.value?.split("-")[0]
            }?parentCategoryId=${familyId}`,
            { headers: { Authorization: `Bearer ${token}`, Accept: "*/*" } }
          )
            .then((response) => {
              // if product's value is a sub-category, compare value.parentCategoryId with data list parent.id
              // if product's value is a parent-category, compare value.id with data list parent.id
              const category = response.data.filter((item) => {
                return (
                  value?.value?.categoryEvollis?.parentCategoryId === item.parent.id ||
                  value?.value?.categoryEvollis?.id === item.parent.id
                )
              })

              return category?.[0]?.parent?.id
            })
            .catch((_errorCategoryField) => console.log("_errorCategoryField : ", _errorCategoryField))
        } else {
          return null
        }
      }

      const getProductPriceFromFormOrCatalog = async () => {
        if (!value) return null
        const { shop } = allValues
        const productFromForm = allValues?.products?.find((x) => x?.sku?.label === value.label)

        const shopId = shop?.value?.split("-")[0]

        if (tenant !== TENANTS.RETAIL_IT) return value?.value?.priceIncTaxes

        const isProductPriceAlreadyFilled =
          productFromForm &&
          productFromForm.price !== undefined &&
          productFromForm.price !== "" &&
          productFromForm.price !== null

        const API_URL_GET_CHECK_PRICE_FROM_CATALOG = `${apiUrl}middle-business-service/api/v1/beneficiary-configuration/check-price-from-catalog/${shopId}`
        return Axios.get(API_URL_GET_CHECK_PRICE_FROM_CATALOG, {
          headers: { Authorization: `Bearer ${token}`, Accept: "*/*" }
        })
          .then((response) => {
            const isCatalogPriceShown = response.data

            if (isProductPriceAlreadyFilled) return productFromForm.price

            return isCatalogPriceShown ? value?.value?.priceIncTaxes : null
          })
          .catch((error) => console.error("Error in checking price from catalog API", error))
      }

      const familyId = await getFamilyField()

      return {
        [familyField]: (await getFamilyField()) || null,
        [categoryField]: familyId ? await getCategoryField(familyId) : null,
        [priceField]: (await getProductPriceFromFormOrCatalog()) || null,
        [brandField]: value?.value ? value?.value?.brand : null,
        [descriptionField]: value?.value ? value?.value?.designation : null,
        [quantityField]: value?.value ? allValues?.products[productIndex].quantity || "1" : "1"
      }
    }
  }
}

export default (tunnelData, token) => {
  return createDecorator(
    shopDecorator(tunnelData, token),
    skuDecorator(tunnelData, token),
    deliveryDecorator(tunnelData, token)
  )
}
